// Boards: Account, AccountUsernameTaken, DeleteAccount.
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { t, tx } from '../../i18n';
import { Bdi, textDir } from '../../i18n/direction';
import { BackIcon, GeneralError, MainButton, WarnFilledIcon, isBlank, onTap } from './parts';
import LanguageMenu from './LanguageMenu';

export default function Account({ account, onBack, onSaved, onDeleted, onAuthFailure }) {
  const [username, setUsername] = useState(account.username);
  const [error, setError] = useState(null); // 'taken' | 'general'
  const busy = useRef(false);
  const [preview, setPreview] = useState(null); // the delete sheet's decks; null while it is closed
  const [deleteFailed, setDeleteFailed] = useState(false);
  const deleting = useRef(false); // one preview or delete request at a time

  const once = async request => {
    if (deleting.current) return;
    deleting.current = true;
    try {
      await request();
    } finally {
      deleting.current = false;
    }
  };

  const openDelete = () => once(async () => {
    setDeleteFailed(false);
    try {
      const res = await axios.get('me/deletion');
      setPreview(res.data.decks);
    } catch (e) {
      if (!(await onAuthFailure(e))) setDeleteFailed(true);
    }
  });

  const deleteAccount = () => once(async () => {
    try {
      await axios.delete('me');
    } catch (e) {
      if (!(await onAuthFailure(e))) {
        setPreview(null);
        setDeleteFailed(true);
      }
      return;
    }
    await onDeleted();
  });

  const save = async () => {
    if (busy.current) return;
    busy.current = true;
    setError(null);
    try {
      const res = await axios.put('me/username', { username });
      onSaved(res.data);
    } catch (e) {
      if (!(await onAuthFailure(e))) setError(e.response?.status === 409 ? 'taken' : 'general');
    } finally {
      busy.current = false;
    }
  };

  const taken = error === 'taken';
  const hidden = preview ? 'true' : undefined;
  return (
    <main className="screen">
      <header className="topbar" aria-hidden={hidden}>
        <a className="icon-btn" href="#back" onClick={onTap(onBack)} aria-label={t('common.back')}><BackIcon /></a>
        <h1 className="topbar-title">{t('home.myAccount')}</h1>
        <LanguageMenu />
      </header>
      <div className="body" aria-hidden={hidden} style={{ gap: 18, paddingTop: 24, paddingBottom: 28 }}>
        <div className="field">
          <label className="label" htmlFor="username">{t('common.username')}</label>
          <input id="username" className={taken ? 'input input-error' : 'input'} dir={textDir(username)} value={username} maxLength={40}
            aria-invalid={taken ? 'true' : undefined} aria-describedby={taken ? 'taken' : undefined}
            onChange={e => setUsername(e.target.value)} />
          {taken && <p id="taken" className="err" role="alert"><WarnFilledIcon />{t('account.taken')}</p>}
          <p className="hint">{t('account.hint')}</p>
        </div>
        <MainButton enabledClass="btn-dark" disabled={isBlank(username)} onClick={save}>{t('common.save')}</MainButton>
        {error === 'general' && <GeneralError />}
        <div style={{ flex: 1 }}></div>
        <a className="btn btn-danger btn-block" href="#delete" onClick={onTap(openDelete)}>{t('account.delete')}</a>
        {deleteFailed && <GeneralError />}
      </div>
      {preview && (
        <>
          <div className="scrim"></div>
          <section className="sheet delete-sheet" role="dialog" aria-modal="true" aria-labelledby="del-q" aria-describedby="del-d">
            <span className="grab" aria-hidden="true"></span>
            <p id="del-q" className="h-l" style={{ textAlign: 'center' }}>{t('account.deleteConfirm')}</p>
            <p id="del-d" style={{ margin: 0, font: '400 16px/1.5 var(--sans)' }}>{t('account.deleteText')}</p>
            {preview.length > 0 && (
              <ul className="list">
                {preview.map(d => (
                  <li key={d.id} className="item" style={{ minHeight: 56 }}>
                    <span className={d.type === 'question' ? 'swatch swatch-q' : 'swatch swatch-a'} aria-hidden="true"></span>
                    <span className="item-main">
                      <span className="item-title"><Bdi>{d.name}</Bdi></span>
                      {d.heir
                        ? <span className="item-sub">{tx('account.passesTo', { name: d.heir })}</span>
                        : <span className="item-sub">{t('account.deleted')}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              <a className="btn btn-primary" href="#delete-yes" onClick={onTap(deleteAccount)}>{t('account.deleteYes')}</a>
              <a className="btn" href="#delete-no" onClick={onTap(() => { if (!deleting.current) setPreview(null); })}>{t('account.deleteNo')}</a>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
